import { MutationTree } from 'vuex';
import { ILoadPayload } from '../types/load-payload';
import { EContractManagementMutation } from './contract-management.constants';
import { IContractManagementState } from './contract-management.types';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { IEditDocumentsPayload } from '@/contract-management/types/edit-documents-payload';
import { IFolderTree } from '@/folders/types/folder-tree';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { ITag } from '@lib-shared/types/Tag';

export const mutations: MutationTree<IContractManagementState> = {
  [EContractManagementMutation.SetDocumentNotifications](
    state: IContractManagementState,
    documentNotifications: IDocumentNotification[]
  ) {
    state.documentNotifications = documentNotifications;
  },
  [EContractManagementMutation.AddDocumentNotifications](
    state: IContractManagementState,
    documentNotifications: IDocumentNotification[]
  ) {
    state.documentNotifications = [...state.documentNotifications, ...documentNotifications];
  },
  [EContractManagementMutation.DeleteDocumentNotifications](
    state: IContractManagementState,
    ids: string[]
  ) {
    state.documentNotifications =
      state.documentNotifications.length > 0
        ? state.documentNotifications.filter(
            (documentNotification: IDocumentNotification) =>
              !ids.some((id: string) => id === documentNotification.id)
          )
        : state.documentNotifications;
  },
  [EContractManagementMutation.DeleteDocuments](state: IContractManagementState) {
    state.documents = state.documents.filter(
      (document: IDocument) => !state.selectedDocumentIds.includes(document.id)
    );
  },
  [EContractManagementMutation.EditDocuments](
    state: IContractManagementState,
    payload: IEditDocumentsPayload
  ) {
    state.documents = state.documents.map((document: IDocument) =>
      state.selectedDocumentIds.includes(document.id)
        ? {
            ...document,
            value: payload.value || document.value,
            contractEndDate: payload.contractEndDate || document.contractEndDate,
            contractStartDate: payload.contractStartDate || document.contractStartDate,
            user: payload.user || document.user,
          }
        : document
    );
  },
  [EContractManagementMutation.SetLoadingDocuments](
    state: IContractManagementState,
    loading: boolean
  ) {
    state.loadingDocuments = loading;
  },
  [EContractManagementMutation.SetLoadedDocuments](
    state: IContractManagementState,
    loaded: boolean
  ) {
    state.loadedDocuments = loaded;
  },
  [EContractManagementMutation.SetLoadingKeyValues](
    state: IContractManagementState,
    loading: boolean
  ) {
    state.loadingKeyValues = loading;
  },
  [EContractManagementMutation.SetLoadedKeyValues](
    state: IContractManagementState,
    loaded: boolean
  ) {
    state.loadedKeyValues = loaded;
  },
  [EContractManagementMutation.SetDocuments](
    state: IContractManagementState,
    documents: IDocument[]
  ) {
    state.documents = documents;
  },
  [EContractManagementMutation.SetKeyValues](
    state: IContractManagementState,
    keyValues: IContractManagementKeyValues
  ) {
    state.keyValues = keyValues;
  },
  [EContractManagementMutation.SetFilterRanges](
    state: IContractManagementState,
    filterRanges: IContractManagementFilterRanges
  ) {
    state.filterRanges = filterRanges;
  },
  [EContractManagementMutation.SetTags](state: IContractManagementState, tags: ITag[]) {
    state.tags = tags;
  },
  [EContractManagementMutation.AddTag](state: IContractManagementState, newTag: ITag) {
    if (state.tags) {
      state.tags = [...state.tags, newTag];
      return;
    }
    state.tags = [newTag];
  },
  [EContractManagementMutation.LoadFolderTree](
    state: IContractManagementState,
    folderTree: IFolderTree
  ) {
    state.folderTree = folderTree;
  },
  [EContractManagementMutation.SetSelectedDocuments](
    state: IContractManagementState,
    ids: string[]
  ) {
    state.selectedDocumentIds = ids;
  },
  [EContractManagementMutation.SetPayload](state: IContractManagementState, payload: ILoadPayload) {
    state.payload = payload;
  },
  [EContractManagementMutation.SetFilterActive](
    state: IContractManagementState,
    filterActive: boolean
  ) {
    state.filterActive = filterActive;
  },
};
